import {
  autocompletion,
  closeBrackets,
  closeBracketsKeymap,
  CompletionContext,
  CompletionResult,
} from '@codemirror/autocomplete'
import { defaultKeymap, history, historyKeymap } from '@codemirror/commands'
import { bracketMatching } from '@codemirror/language'
import { linter } from '@codemirror/lint'
import { Annotation, EditorState, StateEffect } from '@codemirror/state'
import { Decoration, EditorView, keymap, tooltips, ViewUpdate } from '@codemirror/view'
import { ErrorToken, ParseableColumn, parseText } from '@doss/common-all'
import { CommonToken, ParseTreeWalker } from 'antlr4'
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react'
import { InputWrapper } from '~/@/components/inputs/base/input-wrapper'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { brightBlue, gray, green, greenSuccess, uiColors } from '~/common/styles/colors'
import { dossFont, dossMonoFont } from '~/common/styles/theme'
import { AutocompleteListener } from './AutocompleteListener'
import { FormulaBuilderProps } from './types'
import {
  addTokenMark,
  autocompleteField,
  columnDecorationPlugin,
  errorClassExtension,
  errorStateField,
  errorWidgetPlugin,
  getDecorationForToken,
  lint,
  markField,
  setErrors,
  triggerAutocompleteEffect,
} from './utils'

const EditorTheme = EditorView.theme({
  '&.cm-editor': {
    background: 'white',
    'border-radius': '2px',
    color: uiColors.content.primary,
    border: `1px solid ${uiColors.border.normal}`,
    minHeight: '40px',
    'font-size': '12px',
    'font-family': dossMonoFont,
    'justify-content': 'center',
  },
  '&.cm-editor.readonly': {
    backgroundColor: uiColors.background.disabled,
    cursor: 'not-allowed',
  },
  '&.cm-editor.error': {
    outline: `1px solid ${uiColors.border.danger}`,
    '&:hover': {
      outline: `1px solid ${uiColors.border.danger}`,
    },
  },
  '&.cm-editor.error.cm-focused': {
    outline: `1px solid ${uiColors.border.danger}`,
    '&:hover': {
      outline: `1px solid ${uiColors.border.danger}`,
    },
  },
  '&.cm-editor.cm-focused': {
    outline: `1px solid ${uiColors.border.focus}`,
    '&:hover': {
      outline: `1px solid ${uiColors.border.focus}`,
    },
  },
  '&.cm-editor:hover': {
    outline: `1px solid ${uiColors.border.hover}`,
  },
  '& .cm-tooltip': {
    backgroundColor: gray[10],
    border: `1px solid ${gray[9]}`,
    color: uiColors.content.white,
    padding: '5px',
    borderRadius: '2px',
    fontFamily: dossFont,
    fontSize: '12px',
    position: 'relative',
    marginTop: '8px', // Add some space above the tooltip
    marginInlineStart: '-10px',
    // Make the main tooltip container much wider
    maxWidth: 'min(600px, 95vw) !important',
    width: 'auto !important',
    minWidth: 'var(--formula-input-width, 300px)',
    // Ensure it doesn't get cut off
    overflow: 'visible !important',
    // The tooltip is mounted to document.body (a sibling of the MUI Dialog's own portal, not a
    // child), so it needs a z-index above MUI's Dialog (1300) to render on top of the "Add
    // Column" modal. 1500 matches MUI's own Tooltip z-index, the highest in its default stack.
    zIndex: 1500,
  },
  '& .cm-tooltip::before': {
    borderColor: 'rgba(0, 0, 0, 0)',
    borderBottomColor: gray[10], // Point upwards
    borderWidth: '6px',
    marginInlineStart: '-6px',
    top: '-12px', // Position above the tooltip
  },
  '& .cm-tooltip::after': {
    borderColor: 'rgba(0, 0, 0, 0)',
    borderBottomColor: gray[10], // Point upwards, match background
    borderWidth: '5px',
    marginInlineStart: '-5px',
    top: '-10px', // Position above the tooltip
  },
  '& .cm-tooltip-arrow': {
    display: 'none', // Hide default arrow
  },
  '& .cm-diagnostic': {
    padding: 0,
    margin: 0,
    '&::before': {
      display: 'none', // Hide the red bar
    },
  },
  '& .cm-diagnostic-error': {
    border: 'none',
  },
  '& .cm-diagnosticSource': {
    display: 'none', // Hide the "formula" source text
  },
  '& .cm-lintPoint': {
    display: 'none',
  },
  '& .cm-line': {
    paddingInlineStart: '8px',
    paddingTop: '0',
    paddingInlineEnd: '30px',
    paddingBottom: '0',
  },
  '.cm-number': {
    color: brightBlue,
  },
  '.cm-boolean': {
    color: brightBlue,
  },
  '.cm-string': {
    color: green[10],
  },
  '.cm-operator': {
    color: gray[10],
  },
  '.cm-parenthesis': {
    color: gray[10],
  },
  '.cm-separator': {
    color: gray[9],
  },
  '.cm-function': {
    color: gray[10],
  },
  '.cm-logic-operator': {
    color: greenSuccess,
  },
  '.cm-comparison-operator': {
    color: gray[10],
  },
  '.column-chip': {
    display: 'inline-block',
    'margin-bottom': '2px',
  },
  '.column-chip .MuiTypography-root': {
    'font-size': '12px',
    'line-height': '1.3',
    'font-family': dossMonoFont,
  },
  '.column-chip-error': {
    display: 'inline-block',
    'border-bottom': '2px dotted red',
  },
  '.column-chip-error .MuiTypography-root': {
    'font-size': '12px',
    'line-height': '1.3',
    'font-family': dossMonoFont,
  },
  '& .custom-autocomplete-tooltip': {
    maxWidth: 'min(600px, 95vw)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  '& .cm-completion': {
    maxWidth: '500px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  '& .cm-completionLabel': {
    maxWidth: '500px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minWidth: '0px',
  },
})

// Annotation used to mark CodeMirror transactions that originate from React prop changes
// rather than user edits, so the updateListener doesn't echo them back via onChange.
const externalValueUpdateAnnotation = Annotation.define<boolean>()

export function FormulaBuilder({
  value,
  onChange,
  onError,
  availableFields,
  currentContextId,
  parentContextId,
  onSubmit,
  onCancel,
  excludeFieldId,
  wrapperClassName,
  errorText,
  helperText,
  label,
  required,
  showDirtyIndicator,
  tooltip,
  languageScope = 'formula',
  allowDateTimeTokens = false,
  allowMultiColumnAggregations = false,
  readOnly = false,
}: FormulaBuilderProps) {
  const editorContainerRef = useRef<HTMLDivElement | null>(null)
  const editorViewRef = useRef<EditorView | null>(null)
  const { t } = useTypedTranslation()

  // CodeMirror's EditorState requires a string document. Guard against a non-string value (e.g. a
  // Formula object flowing in before the config form maps it to its `formulaString`) so the editor
  // degrades to empty instead of throwing `.split is not a function`.
  const safeValue = typeof value === 'string' ? value : ''

  const formulaInputRef = useRef(safeValue)

  // Use refs for callbacks to prevent unnecessary re-renders
  const onChangeRef = useRef(onChange)
  const onErrorRef = useRef(onError)
  const onSubmitRef = useRef(onSubmit)
  const onCancelRef = useRef(onCancel)

  useEffect(() => {
    onChangeRef.current = onChange
    onErrorRef.current = onError
    onSubmitRef.current = onSubmit
    onCancelRef.current = onCancel
  })

  // Filter out the excluded field (e.g., current column being edited)
  // Memoize to prevent unnecessary re-renders and editor recreation
  const filteredFields = useMemo(() => {
    return excludeFieldId
      ? availableFields.filter((field) => field.id !== excludeFieldId)
      : availableFields
  }, [availableFields, excludeFieldId])

  // Convert FormulaFieldReference to ParseableColumn for compatibility with parseText
  const convertToParseableColumns = useCallback(
    (fields: typeof availableFields): ParseableColumn[] => {
      return fields.map((field) => ({
        id: field.id,
        type: field.type,
        lookupColumn: field.lookupColumn
          ? {
              type: field.lookupColumn.type,
            }
          : null,
      }))
    },
    [],
  )

  // Keep track of the last value we processed to avoid unnecessary onChange calls
  const lastProcessedValueRef = useRef(safeValue)

  // Sync the CodeMirror editor when the value prop changes externally (e.g., when user changes
  // the lookup group dropdown). This ensures prop-driven updates are reflected in the editor.
  useLayoutEffect(() => {
    const view = editorViewRef.current
    const nextValue = safeValue

    formulaInputRef.current = nextValue

    // Skip if view doesn't exist yet or content already matches
    if (!view || view.state.doc.toString() === nextValue) {
      return
    }

    // Update the editor content and mark as external (not a user edit) so the updateListener
    // doesn't echo it back via onChange
    view.dispatch({
      changes: { from: 0, to: view.state.doc.length, insert: nextValue },
      annotations: externalValueUpdateAnnotation.of(true),
    })
  }, [safeValue])

  // Update the fields with new formula string and formula input and set formula error
  // if there are any parsing errors or if the formula input is empty
  const updateFieldValues = useCallback(
    (text: string, shouldNotifyChange = true) => {
      // Convert fields to ParseableColumn format for parseText
      const parseableColumns = convertToParseableColumns(availableFields)
      // Parse the document; forms opt into TODAY/NOW, tables into SUMPRODUCT.
      const parseResults = parseText(text, parseableColumns, languageScope, {
        allowDateTimeTokens,
        allowMultiColumnAggregations,
      })

      // Call onChange callback with both string and parsed formula
      // Use ref to avoid recreating this callback when value changes
      if (text !== lastProcessedValueRef.current) {
        lastProcessedValueRef.current = text
        // Only notify parent of changes from user edits, not prop-driven updates
        if (shouldNotifyChange) {
          onChangeRef.current(text, parseResults.formula)
        }

        // Handle empty strings first (before checking parse errors, since parser errors on empty)
        if (text.trim() === '') {
          // Empty is only an error if required
          if (required) {
            onErrorRef.current(t('formula.formula_is_required'))
          } else {
            onErrorRef.current(null)
          }
        } else if (parseResults.errors.length > 0) {
          onErrorRef.current(t('formula.invalid_formula'))
        } else {
          onErrorRef.current(null)
        }
      }

      return parseResults
    },
    [
      availableFields,
      convertToParseableColumns,
      languageScope,
      allowDateTimeTokens,
      allowMultiColumnAggregations,
      required,
      t,
    ],
  )

  // Initialize the code mirror editor
  useEffect(() => {
    if (!editorContainerRef.current) {
      return
    }

    const updateListener = EditorView.updateListener.of((update: ViewUpdate) => {
      formulaInputRef.current = update.state.doc.toString() // Update the formulaInput ref
      // Check if this update came from a prop change (external) or user edit
      const isExternalUpdate = update.transactions.some((tr) =>
        tr.annotation(externalValueUpdateAnnotation),
      )
      const { tokens, errors, tree } = updateFieldValues(formulaInputRef.current, !isExternalUpdate)

      if (update.docChanged) {
        applySyntaxHighlighting(update.view, tokens, errors)
      }

      // Trigger autocompletion on cursor movement
      if (update.selectionSet) {
        const position = update.state.selection.main.head
        const listener = new AutocompleteListener(position)
        ParseTreeWalker.DEFAULT.walk(listener, tree)
        const autocompleteContext = listener.getRelevantContext() || null

        update.view.dispatch({
          effects: triggerAutocompleteEffect.of({
            fields: filteredFields,
            currentContextId,
            autocompleteContext,
            parentContextId,
            languageScope,
            allowDateTimeTokens,
            allowMultiColumnAggregations,
          }),
        })
      }
    })

    const customAutocompleteSource = (context: CompletionContext): CompletionResult | null => {
      const state = context.state
      return state.field(autocompleteField)
    }

    const submitKeymap = keymap.of([
      {
        key: 'Mod-Enter',
        run: () => {
          if (onSubmitRef.current) {
            onSubmitRef.current()
          }
          return true
        },
      },
      {
        key: 'Escape',
        run: () => {
          if (onCancelRef.current) {
            onCancelRef.current()
          }
          return true
        },
      },
    ])

    const startState = EditorState.create({
      doc: formulaInputRef.current || '', // Initial content of the editor
      extensions: [
        EditorTheme,
        EditorState.readOnly.of(readOnly),
        EditorView.editable.of(!readOnly),
        EditorView.editorAttributes.of({
          class: readOnly ? 'readonly' : '',
        }),
        autocompletion({
          override: [customAutocompleteSource],
          activateOnTyping: true,
          tooltipClass: () => 'custom-autocomplete-tooltip',
          closeOnBlur: true,
        }),
        tooltips({ parent: document.body }),
        linter(lint),
        updateListener,
        markField,
        errorStateField,
        autocompleteField,
        errorClassExtension,
        errorWidgetPlugin(errorStateField),
        columnDecorationPlugin(filteredFields, currentContextId, errorStateField),
        closeBrackets(),
        submitKeymap,
        keymap.of(defaultKeymap),
        keymap.of(closeBracketsKeymap),
        keymap.of(historyKeymap),
        history({ newGroupDelay: 0 }), // So adjacent events are not grouped together for undo/redo
        bracketMatching(),
        EditorView.lineWrapping,
      ],
    })

    const view = new EditorView({
      state: startState,
      parent: editorContainerRef.current,
    })
    editorViewRef.current = view

    const { tokens, errors } = updateFieldValues(formulaInputRef.current)

    applySyntaxHighlighting(view, tokens, errors)

    // Sync the autocomplete tooltip's min-width to the editor's width
    const resizeObserver = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width
      if (width) {
        document.body.style.setProperty('--formula-input-width', `${width}px`)
      }
    })
    resizeObserver.observe(view.dom)

    return () => {
      resizeObserver.disconnect()
      if (editorViewRef.current === view) {
        editorViewRef.current = null
      }
      view.destroy() // Cleanup editor view when component unmounts
    }
  }, [
    filteredFields,
    updateFieldValues,
    currentContextId,
    parentContextId,
    languageScope,
    allowDateTimeTokens,
    allowMultiColumnAggregations,
    readOnly,
  ])

  const applySyntaxHighlighting = (
    view: EditorView,
    tokens: CommonToken[],
    errors: ErrorToken[],
  ) => {
    // Initialize the effects array
    const effects: StateEffect<
      | ErrorToken[]
      | {
          from: number
          to: number
          mark?: Decoration
        }
    >[] = [setErrors.of(errors)]

    // Apply token decorations
    tokens.forEach((token) => {
      const mark = getDecorationForToken(token)
      effects.push(addTokenMark.of({ from: token.start, to: token.stop + 1, mark }))
    })

    const transaction = view.state.update({
      effects,
    })

    view.dispatch(transaction)
  }

  return (
    <InputWrapper
      errorText={errorText}
      helperText={helperText}
      label={label}
      required={required}
      showDirtyIndicator={showDirtyIndicator}
      tooltip={tooltip}
      wrapperClassName={wrapperClassName}
    >
      <div ref={editorContainerRef} />
    </InputWrapper>
  )
}
